// White 70px topbar — WhatChimp design system parity (WP-00).
// Features: burger (mobile), 3 usage rings (28px conic), search menus input,
// account switcher, theme toggle, language switch, help menu, notifications bell, profile menu.
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../lib/firebase';
import { useAuth } from '../../lib/useAuth';
import { useAdminAccess } from '../../lib/platformAdmins';
import { useI18n } from '../../lib/i18n';
import { useTheme } from '../../lib/theme';
import { cn } from '../../lib/cn';
import { useUsageSummary } from '../../lib/usage';
import { NAV_ITEMS } from '../../lib/nav';
import UsageRing from './UsageRing';
import HelpMenu from './HelpMenu';
import AccountSwitcher from './AccountSwitcher';
import NotificationsDropdown from './NotificationsDropdown';
import {
  MenuIcon,
  SearchIcon,
  SunIcon,
  MoonIcon,
  ShieldIcon,
  LogOutIcon,
  UserIcon,
} from '../icons';

function SearchMenus() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = query.trim()
    ? NAV_ITEMS.filter((item) => {
        const label = t(item.key).toLowerCase();
        return label.includes(query.toLowerCase());
      })
    : [];

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  return (
    <div ref={containerRef} className="relative hidden lg:block w-48 xl:w-56">
      <div className="relative">
        <SearchIcon
          size={14}
          className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-ink-3 dark:text-slate-400"
        />
        <input
          type="text"
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          placeholder={t('ui.searchMenus') || 'Search menus…'}
          className={cn(
            'w-full rounded-full border border-line bg-slate-50 py-1.5 ps-8 pe-3 text-xs text-ink transition-colors',
            'placeholder:text-ink-4 outline-none focus:border-brand focus:bg-white focus:ring-1 focus:ring-brand',
            'dark:border-slate-800 dark:bg-slate-800 dark:text-slate-200 dark:placeholder:text-slate-500 dark:focus:bg-slate-900',
          )}
        />
      </div>

      {open && filtered.length > 0 && (
        <div className="absolute start-0 top-full z-30 mt-1.5 w-60 overflow-hidden rounded-card border border-line bg-white py-1 shadow-xl dark:border-slate-800 dark:bg-slate-900">
          {filtered.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.path}
                type="button"
                onClick={() => {
                  setOpen(false);
                  setQuery('');
                  navigate(item.path);
                }}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-start text-xs font-medium text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <Icon size={16} className={cn('shrink-0', item.color || 'text-slate-500')} />
                <span className="truncate">{t(item.key)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ProfileMenu() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const access = useAdminAccess(user);
  const [open, setOpen] = useState(false);

  const email = user?.email || '';
  const initial = email.charAt(0).toUpperCase() || 'U';

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('topbar.profile')}
        className="grid h-9 w-9 place-items-center rounded-full bg-brand-50 text-xs font-bold text-brand ring-2 ring-transparent transition-all hover:ring-brand/30 dark:bg-slate-800 dark:text-brand-tint"
      >
        {initial}
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 cursor-default"
          />
          <div
            role="menu"
            className="absolute end-0 top-full z-50 mt-2 w-56 overflow-hidden rounded-card border border-line bg-white py-1 text-ink shadow-xl dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
          >
            <div className="truncate px-3.5 py-2.5 text-xs font-medium text-ink-3 dark:text-slate-400" title={email}>
              <div className="font-bold text-ink dark:text-slate-200 truncate">{email}</div>
              <div className="text-[11px] text-ink-4 dark:text-slate-500">WaTag Workspace</div>
            </div>

            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                navigate('/settings');
              }}
              className="flex w-full items-center gap-2.5 border-t border-line px-3.5 py-2 text-start text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-800 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <UserIcon size={15} className="text-slate-400" />
              <span>{t('nav.settings')}</span>
            </button>

            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                navigate('/billing');
              }}
              className="flex w-full items-center gap-2.5 px-3.5 py-2 text-start text-xs font-medium text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <span className="text-xs text-slate-400">💳</span>
              <span>{t('nav.billing')}</span>
            </button>

            {/* Only Tagnologys staff (root allowlist or platformAdmin claim) see this. */}
            {access.allowed && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  navigate('/padmin');
                }}
                className="flex w-full items-center gap-2.5 border-t border-line px-3.5 py-2 text-start text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-800 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <ShieldIcon size={15} className="text-brand" />
                <span>{t('topbar.console')}</span>
              </button>
            )}

            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                void signOut(auth);
              }}
              className="flex w-full items-center gap-2.5 border-t border-line px-3.5 py-2 text-start text-xs font-medium text-danger hover:bg-rose-50 dark:border-slate-800 dark:hover:bg-rose-950/30"
            >
              <LogOutIcon size={15} />
              <span>{t('dash.signout')}</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function Topbar({ onMenu }: { onMenu: () => void }) {
  const { t, lang, setLang } = useI18n();
  const { resolvedTheme, toggleTheme } = useTheme();
  const { rings } = useUsageSummary();

  return (
    <header className="sticky top-0 z-20 flex h-[70px] items-center gap-3 border-b border-line bg-white px-4 transition-colors sm:gap-4 sm:px-6 dark:border-slate-800 dark:bg-slate-900">
      <button
        type="button"
        onClick={onMenu}
        aria-label={t('topbar.openMenu')}
        className="grid h-9 w-9 place-items-center rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 md:hidden"
      >
        <MenuIcon size={20} />
      </button>

      {/* Usage rings (28px conic dials) */}
      <div className="flex items-center gap-3 sm:gap-5">
        {rings.map((ring, i) => (
          <UsageRing
            key={ring.key}
            data={ring}
            // AI-token ring is hidden on the narrowest screens (mockup parity).
            className={cn(i === 2 && 'hidden sm:flex')}
          />
        ))}
      </div>

      <div className="flex-1" />

      {/* Quick Search menus box */}
      <SearchMenus />

      {/* WhatsApp Account Switcher */}
      <AccountSwitcher />

      {/* Dark/Light mode toggle */}
      <button
        type="button"
        onClick={toggleTheme}
        aria-label="Toggle theme"
        className="grid h-9 w-9 place-items-center rounded-full border border-line bg-slate-50 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
      >
        {resolvedTheme === 'dark' ? <SunIcon size={16} /> : <MoonIcon size={16} />}
      </button>

      {/* Language switcher */}
      <button
        type="button"
        onClick={() => setLang(lang === 'en' ? 'ar' : 'en')}
        className="rounded-full border border-line bg-slate-50 px-3 py-1.5 text-xs font-bold text-slate-700 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
      >
        {t('lang.switch')}
      </button>

      {/* Help menu */}
      <HelpMenu />

      {/* Live notification bell dropdown */}
      <NotificationsDropdown />

      {/* User profile menu */}
      <ProfileMenu />
    </header>
  );
}
